import {
  // KoButton,
  KoCharts,
  KoLoader,
  KoSelect,
  KoSkeleton,
  KoText,
  KoTitle,
  KoToast
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { MonitoringAPI } from '../services/monitoring.service';
import { TIME_RANGE_OPTIONS } from '../constants';
import { metricsLineChart } from '../charts/metricsLineChart';
import { errorRateChart } from '../charts/errorRateChart';
import { latencyDistributionChart } from '../charts/latencyDistributionChart';
import { resourceUsageChart } from '../charts/resourceUsageChart';
import MonitoringSubNav from './MonitoringSubNav';
import styles from './monitoring.module.scss';

const MOCK_SERVICE_LIST = [
  { label: 'api-gateway', value: 'svc-001' },
  { label: 'auth-service', value: 'svc-002' },
  { label: 'payment-processor', value: 'svc-003' },
  { label: 'search-engine', value: 'svc-006' },
  { label: 'analytics-pipeline', value: 'svc-008' }
];

const MOCK_METRICS_DATA = {
  summary: { rps: 1520, errorRate: 0.45, p95: 142, cpu: 38.5 },
  requestData: [
    { timestamp: '14:00', http_requests: 1240 }, { timestamp: '14:05', http_requests: 1380 },
    { timestamp: '14:10', http_requests: 1520 }, { timestamp: '14:15', http_requests: 1100 },
    { timestamp: '14:20', http_requests: 1680 }, { timestamp: '14:25', http_requests: 1450 },
    { timestamp: '14:30', http_requests: 1320 }, { timestamp: '14:35', http_requests: 1590 },
    { timestamp: '14:40', http_requests: 1270 }, { timestamp: '14:45', http_requests: 1410 },
    { timestamp: '14:50', http_requests: 1550 }, { timestamp: '14:55', http_requests: 1480 }
  ],
  errorData: [
    { timestamp: '14:00', http4xx: 8, http5xx: 2, timeout: 1, connectionError: 0 },
    { timestamp: '14:05', http4xx: 12, http5xx: 3, timeout: 0, connectionError: 1 },
    { timestamp: '14:10', http4xx: 15, http5xx: 5, timeout: 2, connectionError: 0 },
    { timestamp: '14:15', http4xx: 6, http5xx: 1, timeout: 0, connectionError: 0 },
    { timestamp: '14:20', http4xx: 18, http5xx: 4, timeout: 1, connectionError: 1 },
    { timestamp: '14:25', http4xx: 10, http5xx: 2, timeout: 0, connectionError: 0 },
    { timestamp: '14:30', http4xx: 9, http5xx: 3, timeout: 1, connectionError: 0 },
    { timestamp: '14:35', http4xx: 14, http5xx: 4, timeout: 2, connectionError: 1 },
    { timestamp: '14:40', http4xx: 7, http5xx: 1, timeout: 0, connectionError: 0 },
    { timestamp: '14:45', http4xx: 11, http5xx: 3, timeout: 1, connectionError: 0 },
    { timestamp: '14:50', http4xx: 13, http5xx: 4, timeout: 1, connectionError: 1 },
    { timestamp: '14:55', http4xx: 10, http5xx: 2, timeout: 0, connectionError: 0 }
  ],
  latencyDistribution: [
    { timestamp: '14:00', latency_p50: 22, latency_p95: 145, latency_p99: 320 },
    { timestamp: '14:05', latency_p50: 25, latency_p95: 160, latency_p99: 350 },
    { timestamp: '14:10', latency_p50: 30, latency_p95: 180, latency_p99: 420 },
    { timestamp: '14:15', latency_p50: 20, latency_p95: 130, latency_p99: 280 },
    { timestamp: '14:20', latency_p50: 35, latency_p95: 200, latency_p99: 450 },
    { timestamp: '14:25', latency_p50: 28, latency_p95: 155, latency_p99: 340 },
    { timestamp: '14:30', latency_p50: 24, latency_p95: 142, latency_p99: 310 },
    { timestamp: '14:35', latency_p50: 32, latency_p95: 175, latency_p99: 400 },
    { timestamp: '14:40', latency_p50: 21, latency_p95: 128, latency_p99: 270 },
    { timestamp: '14:45', latency_p50: 26, latency_p95: 148, latency_p99: 330 },
    { timestamp: '14:50', latency_p50: 29, latency_p95: 165, latency_p99: 370 },
    { timestamp: '14:55', latency_p50: 23, latency_p95: 138, latency_p99: 290 }
  ],
  resourceData: [
    { timestamp: '14:00', cpu: 32, memory: 58, connections: 1240 },
    { timestamp: '14:05', cpu: 35, memory: 59, connections: 1380 },
    { timestamp: '14:10', cpu: 41, memory: 61, connections: 1520 },
    { timestamp: '14:15', cpu: 38, memory: 60, connections: 1100 },
    { timestamp: '14:20', cpu: 45, memory: 63, connections: 1680 },
    { timestamp: '14:25', cpu: 42, memory: 62, connections: 1450 },
    { timestamp: '14:30', cpu: 36, memory: 59, connections: 1320 },
    { timestamp: '14:35', cpu: 39, memory: 61, connections: 1590 },
    { timestamp: '14:40', cpu: 33, memory: 58, connections: 1270 },
    { timestamp: '14:45', cpu: 37, memory: 60, connections: 1410 },
    { timestamp: '14:50', cpu: 40, memory: 62, connections: 1550 },
    { timestamp: '14:55', cpu: 34, memory: 59, connections: 1480 }
  ]
};

function LiveMetrics() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [serviceList, setServiceList] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [metricsData, setMetricsData] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isAutoRefresh, setAutoRefresh] = useState(true);
  const refreshRef = useRef(null);

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/monitoring`
    : '/monitoring';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Monitoring', path: basePath },
      { title: 'Live Metrics' }
    ]));
    fetchServiceList();
  }, [workspaceId]);

  useEffect(() => {
    if (selectedService) {
      fetchMetrics();
    }
  }, [selectedService, selectedTimeRange]);

  useEffect(() => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    if (isAutoRefresh && selectedService) {
      refreshRef.current = setInterval(fetchMetrics, 30000);
    }
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [isAutoRefresh, selectedService, selectedTimeRange]);

  const fetchServiceList = () => {
    MonitoringAPI.getServiceList().then(
      (data) => {
        const raw = (data?.data || data || []).map(s => ({
          label: s.serviceName || s.name,
          value: s.serviceId || s._id
        }));
        const list = raw.length > 0 ? raw : MOCK_SERVICE_LIST;
        setServiceList(list);
        if (list.length > 0 && !selectedService) {
          setSelectedService(list[0].value);
        }
      },
      () => {
        setServiceList(MOCK_SERVICE_LIST);
        if (!selectedService) {
          setSelectedService(MOCK_SERVICE_LIST[0].value);
        }
        KoToast.error('Failed to load service list.');
      }
    );
  };

  const fetchMetrics = useCallback(() => {
    if (!selectedService) return;
    setLoading(true);
    MonitoringAPI.getLiveMetrics(selectedService, { timeRange: selectedTimeRange }).then(
      (res) => {
        const data = res?.summary ? res : MOCK_METRICS_DATA;
        setMetricsData(data);
        setLoading(false);
      },
      () => {
        setMetricsData(MOCK_METRICS_DATA);
        setLoading(false);
        KoToast.error('Failed to load metrics.');
      }
    );
  }, [selectedService, selectedTimeRange]);

  const summary = metricsData?.summary || {};

  const quickStats = [
    { label: 'Requests/sec', value: summary.rps ?? '—', icon: 'LineChartOutlined', bg: 'var(--palette-primary-lighter)' },
    { label: 'Error Rate', value: summary.errorRate != null ? `${summary.errorRate.toFixed(2)}%` : '—', icon: 'WarningOutlined', bg: 'rgba(245, 34, 45, 0.1)' },
    { label: 'P95 Latency', value: summary.p95 != null ? `${summary.p95}ms` : '—', icon: 'ClockCircleOutlined', bg: 'rgba(114, 46, 209, 0.1)' },
    { label: 'CPU Usage', value: summary.cpu != null ? `${summary.cpu.toFixed(1)}%` : '—', icon: 'DashboardOutlined', bg: 'rgba(19, 194, 194, 0.1)' }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <MonitoringSubNav activeKey='live-metrics' />

      <KoToolBar
        title='Live Metrics'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        extraComponentBefore={
          <div className={styles.metricsHeader}>
            <KoSelect
              value={selectedService}
              onChange={setSelectedService}
              placeholder='Select service'
              options={serviceList}
              style={{ minWidth: 200 }}
            />
            <KoSelect
              value={selectedTimeRange}
              onChange={setSelectedTimeRange}
              options={TIME_RANGE_OPTIONS}
              style={{ minWidth: 160 }}
            />
            {isAutoRefresh && (
              <div className={styles.refreshIndicator}>
                <span className={styles.refreshDot} />
                <KoText text='Auto-refresh 30s' />
              </div>
            )}
          </div>
        }
        buttonList={[
          {
            label: isAutoRefresh ? 'Pause' : 'Resume',
            icon: <KoIcon color='#565555' name={isAutoRefresh ? 'PauseOutlined' : 'CaretRightOutlined'} width={16} height={16} />,
            onClick: () => setAutoRefresh(p => !p)
          },
          {
            label: 'Refresh',
            icon: <KoIcon color='#565555' name='ReloadOutlined' width={16} height={16} />,
            onClick: fetchMetrics
          }
        ]}
      />

      {/* Quick Stats */}
      <div className={styles.statsGrid}>
        {quickStats.map((s) => (
          <div key={s.label} className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: s.bg }}>
              <KoIcon name={s.icon} width={22} height={22} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{s.value}</span>
              <span className={styles.statLabel}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className={styles.metricsGrid}>
        <div className={styles.chartCard}>
          <KoTitle level={5} text='HTTP Requests Over Time' className={styles.chartTitle} />
          {!metricsData?.requestData && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {metricsData?.requestData && <KoCharts option={metricsLineChart(metricsData.requestData, ['http_requests'])} />}
        </div>
        <div className={styles.chartCard}>
          <KoTitle level={5} text='Error Rate' className={styles.chartTitle} />
          {!metricsData?.errorData && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {metricsData?.errorData && <KoCharts option={errorRateChart(metricsData.errorData)} />}
        </div>
        <div className={styles.chartCard}>
          <KoTitle level={5} text='Latency Distribution' className={styles.chartTitle} />
          {!metricsData?.latencyDistribution && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {metricsData?.latencyDistribution && <KoCharts option={latencyDistributionChart(metricsData.latencyDistribution)} />}
        </div>
        <div className={styles.chartCard}>
          <KoTitle level={5} text='Resource Usage' className={styles.chartTitle} />
          {!metricsData?.resourceData && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {metricsData?.resourceData && <KoCharts option={resourceUsageChart(metricsData.resourceData)} />}
        </div>
      </div>
    </div>
  );
}

export default LiveMetrics;
